import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, RefreshCw, Navigation, Route as RouteIcon, X } from 'lucide-react';
import { usersApi } from '../services/api';

const TILE_URL = 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png';
const TILE_ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>';

/** Everyone counts as live within this window; beyond it the fix is stale. */
const LIVE_WINDOW_MIN = 15;
/** A jump faster than this is a bad satellite fix, not travel. */
const MAX_KMH = 120;

type Mode = 'live' | 'tracking';

const timeAgo = (iso?: string | null): string => {
    if (!iso) return 'never';
    const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
};

const isLive = (iso?: string | null) =>
    !!iso && (Date.now() - new Date(iso).getTime()) / 60000 <= LIVE_WINDOW_MIN;

/** "143d ago" hides which day it was — a stale fix needs the actual date. */
const formatStamp = (iso?: string | null): string => {
    if (!iso) return 'No sync recorded';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return 'No sync recorded';
    return d.toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: true,
    });
};

const clock = (iso: string) =>
    new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });

const metres = (aLat: number, aLng: number, bLat: number, bLng: number) => {
    const R = 6371000;
    const dLat = (bLat - aLat) * Math.PI / 180;
    const dLng = (bLng - aLng) * Math.PI / 180;
    const p = Math.sin(dLat / 2) ** 2 +
        Math.cos(aLat * Math.PI / 180) * Math.cos(bLat * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(p));
};

const pin = (live: boolean, letter: string) => L.divIcon({
    className: '',
    html: `<div style="width:30px;height:30px;border-radius:50%;background:${live ? '#4f46e5' : '#94a3b8'};color:#fff;
        border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.35);display:flex;align-items:center;
        justify-content:center;font-weight:900;font-size:13px">${letter}</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
});

const stopPin = (seq: number) => L.divIcon({
    className: '',
    html: `<div style="width:26px;height:26px;border-radius:50%;background:#4f46e5;color:#fff;border:3px solid #fff;
        box-shadow:0 2px 8px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;
        font-weight:900;font-size:11px">${seq}</div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13],
});

/** Frames the given points once per change, so the view always opens usable. */
const FitTo: React.FC<{ points: [number, number][]; token: string }> = ({ points, token }) => {
    const map = useMap();
    useEffect(() => {
        if (points.length === 0) return;
        if (points.length === 1) map.setView(points[0], 15);
        else map.fitBounds(L.latLngBounds(points), { padding: [60, 60] });
    }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
    return null;
};

/** Flies to whoever or whatever was clicked. */
const FlyTo: React.FC<{ target: [number, number] | null }> = ({ target }) => {
    const map = useMap();
    useEffect(() => {
        if (target) map.flyTo(target, 17, { duration: 0.8 });
    }, [target, map]);
    return null;
};

/**
 * One screen, two questions.
 *
 * "Live" answers where the whole team is right now. "Tracking" answers what one
 * rep's day looked like. They share the roster, the map and the same selection,
 * so switching between them is a toggle rather than a different page — which is
 * how people actually use it: spot someone on the live map, then look at their day.
 */
const LiveLocation: React.FC = () => {
    const [mode, setMode] = useState<Mode>('live');
    const [users, setUsers] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [flyTarget, setFlyTarget] = useState<[number, number] | null>(null);

    // undefined = not looked up yet, null = looked up and nothing came back.
    const [addresses, setAddresses] = useState<Record<string, string | null>>({});

    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [history, setHistory] = useState<any[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [stops, setStops] = useState<any[]>([]);
    const [stopsLoading, setStopsLoading] = useState(false);
    const [stopsError, setStopsError] = useState<string | null>(null);
    const [stopsOpen, setStopsOpen] = useState(true);

    const loadTeam = useCallback(async (quiet = false) => {
        if (!quiet) setLoading(true);
        try {
            const res = await usersApi.getNetwork();
            if (!res?.success || !Array.isArray(res.data)) throw new Error('The server returned no team locations.');
            setUsers(res.data);
            setError(null);
        } catch (e: any) {
            setError(e?.message || 'Could not load team locations.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadTeam();
        // Only the live view refreshes itself; a route being read must not move.
        const iv = setInterval(() => { if (mode === 'live') loadTeam(true); }, 60000);
        return () => clearInterval(iv);
    }, [loadTeam, mode]);

    const selected = users.find(u => String(u.id) === selectedId) || null;

    // ── Tracking data ───────────────────────────────────────────────────────
    useEffect(() => {
        if (mode !== 'tracking' || !selected) { setHistory([]); return; }
        let cancelled = false;
        setHistoryLoading(true);
        usersApi.getLocationHistory(selected.id, date)
            .then(res => {
                if (cancelled) return;
                const raw = (res?.success && Array.isArray(res.data) ? res.data : [])
                    .slice()
                    .sort((a, b) => new Date(a.recorded_at).getTime() - new Date(b.recorded_at).getTime());

                // Drop impossible jumps: a cell-tower fix can land hundreds of km
                // away and would otherwise draw a line across the country.
                const clean: any[] = [];
                raw.forEach((p, i) => {
                    if (i === 0) { clean.push(p); return; }
                    const prev = clean[clean.length - 1];
                    const secs = (new Date(p.recorded_at).getTime() - new Date(prev.recorded_at).getTime()) / 1000;
                    const dist = metres(Number(prev.latitude), Number(prev.longitude), Number(p.latitude), Number(p.longitude));
                    if (secs <= 0 || (dist / secs) * 3.6 <= MAX_KMH) clean.push(p);
                });
                setHistory(clean);
            })
            .catch(() => { if (!cancelled) setHistory([]); })
            .finally(() => { if (!cancelled) setHistoryLoading(false); });
        return () => { cancelled = true; };
    }, [mode, selected?.id, date]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (mode !== 'tracking' || !selected) { setStops([]); return; }
        let cancelled = false;
        setStopsLoading(true);
        setStopsError(null);
        setStops([]);
        usersApi.getLocationStops(selected.id, date)
            .then(res => {
                if (cancelled) return;
                if (!res?.success || !Array.isArray(res.data)) throw new Error('The server returned no stops.');
                setStops(res.data);
            })
            .catch((e: any) => {
                if (cancelled) return;
                // "No stops" is a claim about someone's day — a failed call must
                // not be allowed to make it.
                setStopsError(e?.message || 'Could not load stops.');
            })
            .finally(() => { if (!cancelled) setStopsLoading(false); });
        return () => { cancelled = true; };
    }, [mode, selected?.id, date]); // eslint-disable-line react-hooks/exhaustive-deps

    const resolveAddress = (id: string, lat: number, lng: number) => {
        if (addresses[id] !== undefined) return;
        usersApi.reverseGeocode(lat, lng)
            .then(res => setAddresses(a => ({ ...a, [id]: res?.data?.address ?? null })))
            .catch(() => setAddresses(a => ({ ...a, [id]: null })));
    };

    const located = useMemo(
        () => users.filter(u => Number.isFinite(Number(u.last_location?.lat)) && Number.isFinite(Number(u.last_location?.lng))),
        [users],
    );

    const path = useMemo<[number, number][]>(
        () => history.map(p => [Number(p.latitude), Number(p.longitude)] as [number, number]),
        [history],
    );

    const trackStats = useMemo(() => {
        let dist = 0;
        for (let i = 1; i < path.length; i++) dist += metres(path[i - 1][0], path[i - 1][1], path[i][0], path[i][1]);
        const mins = history.length > 1
            ? Math.round((new Date(history[history.length - 1].recorded_at).getTime() - new Date(history[0].recorded_at).getTime()) / 60000)
            : 0;
        return {
            dist,
            duration: mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`,
            pings: history.length,
        };
    }, [path, history]);

    const fitPoints = mode === 'live'
        ? located.map(u => [Number(u.last_location.lat), Number(u.last_location.lng)] as [number, number])
        : path;
    const fitToken = mode === 'live' ? `live-${located.length}` : `track-${selected?.id}-${date}-${path.length}`;
    const liveCount = located.filter(u => isLive(u.last_location_at)).length;

    return (
        <div className="h-full flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between gap-3 px-3 md:px-4 py-3 border-b border-slate-200 bg-white flex-shrink-0">
                <div className="flex items-center gap-2.5 min-w-0">
                    <div className="p-2 bg-indigo-600 rounded-xl shadow-lg shadow-indigo-200 flex-shrink-0">
                        <MapPin className="h-4 w-4 text-white" />
                    </div>
                    <div className="min-w-0">
                        <h1 className="text-base md:text-lg font-black text-slate-900 leading-tight">
                            {mode === 'live' ? 'Live Location' : 'Tracking'}
                        </h1>
                        <p className="text-[11px] text-slate-400 font-medium truncate">
                            {mode === 'live'
                                ? `${liveCount} of ${located.length} active in the last ${LIVE_WINDOW_MIN} min`
                                : selected ? `${selected.name} · ${stops.length} stop${stops.length === 1 ? '' : 's'}` : 'Pick a team member'}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                    {/* Mode switch */}
                    <div className="bg-slate-100 p-1 rounded-xl flex items-center gap-0.5">
                        <button
                            onClick={() => setMode('live')}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${mode === 'live' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500'}`}
                        >
                            <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> Live</span>
                        </button>
                        <button
                            onClick={() => setMode('tracking')}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${mode === 'tracking' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500'}`}
                        >
                            <span className="inline-flex items-center gap-1.5"><RouteIcon className="h-3.5 w-3.5" /> Tracking</span>
                        </button>
                    </div>

                    {mode === 'tracking' && (
                        <input
                            type="date"
                            value={date}
                            onChange={e => setDate(e.target.value)}
                            className="h-9 px-2 border border-slate-300 rounded-lg text-xs font-bold text-slate-700 outline-none"
                        />
                    )}

                    <button
                        onClick={() => loadTeam()}
                        className="h-9 px-3 inline-flex items-center gap-2 bg-white border border-slate-300 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    >
                        <RefreshCw className={`h-4 w-4 ${loading || historyLoading ? 'animate-spin' : ''}`} />
                        <span className="hidden md:inline">Refresh</span>
                    </button>
                </div>
            </div>

            <div className="flex-1 min-h-0 flex">
                {/* Roster — shared by both modes. */}
                <aside className="hidden md:flex w-64 lg:w-72 flex-col border-r border-slate-200 bg-white flex-shrink-0 overflow-y-auto">
                    {users.map(u => {
                        const has = Number.isFinite(Number(u.last_location?.lat));
                        const live = isLive(u.last_location_at);
                        const active = selectedId === String(u.id);
                        return (
                            <button
                                key={u.id}
                                onClick={() => {
                                    setSelectedId(String(u.id));
                                    if (mode === 'live' && has) setFlyTarget([Number(u.last_location.lat), Number(u.last_location.lng)]);
                                }}
                                className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left border-l-[3px] transition-colors ${active ? 'border-indigo-600 bg-indigo-50' : 'border-transparent hover:bg-slate-50'}`}
                            >
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-black text-sm flex-shrink-0 ${live ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-500'}`}>
                                    {u.name.charAt(0).toUpperCase()}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm font-bold text-slate-800 truncate leading-tight">{u.name}</p>
                                    <p className="text-[10px] text-slate-400 truncate leading-tight">
                                        {has ? timeAgo(u.last_location_at) : 'no location yet'}
                                    </p>
                                </div>
                                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${live ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                            </button>
                        );
                    })}
                </aside>

                {/* Map.
                    `isolate` matters: Leaflet stacks its own panes up to 700, so the
                    stats bar and stops list have to outrank them — but the app nav
                    only reaches z-[70], and without a stacking context of its own
                    those panels would float above the navigation and swallow its
                    dropdowns. Isolating keeps the whole fight inside this box. */}
                <div className="flex-1 min-w-0 relative isolate">
                    {error ? (
                        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                            <p className="text-sm font-semibold text-red-600">{error}</p>
                            <p className="text-xs text-slate-500 mt-1">This is a load failure, not an empty team.</p>
                        </div>
                    ) : mode === 'tracking' && !selected ? (
                        <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm font-medium px-6 text-center">
                            Pick a team member from the list to see their day.
                        </div>
                    ) : (
                        <MapContainer center={[26.1445, 91.7362]} zoom={12} className="h-full w-full" zoomControl={false}>
                            <TileLayer url={TILE_URL} attribution={TILE_ATTR} />
                            <FitTo points={fitPoints} token={fitToken} />
                            <FlyTo target={flyTarget} />

                            {mode === 'live' && located.map(u => {
                                const lat = Number(u.last_location.lat);
                                const lng = Number(u.last_location.lng);
                                const live = isLive(u.last_location_at);
                                return (
                                    <Marker
                                        key={u.id}
                                        position={[lat, lng]}
                                        icon={pin(live, u.name.charAt(0).toUpperCase())}
                                        // Geocoded only when the popup opens: looking up the
                                        // whole team every refresh would spend the whole
                                        // rate limit on pins nobody is reading.
                                        eventHandlers={{ popupopen: () => resolveAddress(u.id, lat, lng) }}
                                    >
                                        <Popup>
                                            <div className="text-xs min-w-[190px]">
                                                <p className="font-black text-slate-900">{u.name}</p>
                                                <p className="text-slate-500 mt-0.5">{live ? 'Live' : 'Last seen'} · {timeAgo(u.last_location_at)}</p>
                                                <p className="text-slate-400 mt-0.5">{formatStamp(u.last_location_at)}</p>
                                                <p className="text-slate-600 mt-1.5 leading-snug break-words">
                                                    {addresses[u.id] === undefined
                                                        ? 'Looking up address…'
                                                        : addresses[u.id] || `${lat.toFixed(5)}, ${lng.toFixed(5)}`}
                                                </p>
                                                <a href={`https://www.google.com/maps?q=${lat},${lng}`} target="_blank" rel="noreferrer"
                                                    className="inline-flex items-center gap-1 mt-1.5 text-indigo-600 font-bold">
                                                    <Navigation className="h-3 w-3" /> Open in Maps
                                                </a>
                                            </div>
                                        </Popup>
                                    </Marker>
                                );
                            })}

                            {mode === 'tracking' && path.length > 1 && (
                                <Polyline positions={path} pathOptions={{ color: '#4f46e5', weight: 4, opacity: 0.75 }} />
                            )}

                            {mode === 'tracking' && stops.map(s => (
                                <Marker key={s.seq} position={[s.lat, s.lng]} icon={stopPin(s.seq)}>
                                    <Popup>
                                        <div className="text-xs min-w-[180px]">
                                            <p className="font-black text-slate-900">Stop {s.seq}</p>
                                            <p className="text-slate-500 mt-0.5">{clock(s.arrived_at)} – {clock(s.left_at)}</p>
                                            <p className="text-indigo-600 font-bold mt-0.5">
                                                {s.minutes >= 60 ? `${Math.floor(s.minutes / 60)}h ${s.minutes % 60}m` : `${s.minutes}m`} here
                                            </p>
                                            <p className="text-slate-600 mt-1 leading-snug break-words">
                                                {s.address || `${s.lat.toFixed(5)}, ${s.lng.toFixed(5)}`}
                                            </p>
                                        </div>
                                    </Popup>
                                </Marker>
                            ))}
                        </MapContainer>
                    )}

                    {/* Tracking stats + stops */}
                    {mode === 'tracking' && selected && !historyLoading && history.length > 0 && (
                        <div className="absolute bottom-3 left-3 right-3 md:right-auto z-[500]">
                            <div className="bg-white/95 backdrop-blur shadow-lg border border-slate-200 px-4 py-2.5 rounded-2xl flex items-center gap-5 overflow-x-auto">
                                <div className="flex flex-col flex-shrink-0">
                                    <span className="text-[9px] font-black text-indigo-500 uppercase tracking-widest">Distance</span>
                                    <span className="text-base font-black text-slate-900">
                                        {trackStats.dist >= 1000 ? `${(trackStats.dist / 1000).toFixed(2)} km` : `${Math.round(trackStats.dist)} m`}
                                    </span>
                                </div>
                                <div className="w-px h-8 bg-slate-200 flex-shrink-0" />
                                <div className="flex flex-col flex-shrink-0">
                                    <span className="text-[9px] font-black text-indigo-500 uppercase tracking-widest">Duration</span>
                                    <span className="text-base font-black text-slate-900">{trackStats.duration}</span>
                                </div>
                                <div className="w-px h-8 bg-slate-200 flex-shrink-0" />
                                <div className="flex flex-col flex-shrink-0">
                                    <span className="text-[9px] font-black text-indigo-500 uppercase tracking-widest">Pings</span>
                                    <span className="text-base font-black text-slate-900">{trackStats.pings}</span>
                                </div>
                                <div className="w-px h-8 bg-slate-200 flex-shrink-0" />
                                <div className="flex flex-col flex-shrink-0">
                                    <span className="text-[9px] font-black text-indigo-500 uppercase tracking-widest">Stops</span>
                                    <span className="text-base font-black text-slate-900">{stopsLoading ? '…' : stops.length}</span>
                                </div>
                                <button
                                    onClick={() => setStopsOpen(v => !v)}
                                    className={`ml-1 p-2.5 rounded-xl transition-all flex-shrink-0 ${stopsOpen ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600'}`}
                                    title="Stops: arrival, time spent, address"
                                >
                                    <RouteIcon className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    )}

                    {mode === 'tracking' && selected && stopsOpen && (
                        <div className="absolute top-3 right-3 bottom-24 w-[calc(100%-1.5rem)] md:w-[24rem] z-[500] bg-white/95 backdrop-blur shadow-lg border border-slate-200 rounded-2xl flex flex-col overflow-hidden">
                            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
                                <div>
                                    <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest">Stops</p>
                                    <p className="text-xs font-bold text-slate-500">
                                        {stopsLoading ? 'Looking up addresses…' : `${stops.length} place${stops.length === 1 ? '' : 's'} · 3 min minimum`}
                                    </p>
                                </div>
                                <button onClick={() => setStopsOpen(false)} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100">
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                            <div className="flex-1 overflow-y-auto">
                                {stopsError ? (
                                    <div className="px-4 py-8 text-center">
                                        <p className="text-sm font-semibold text-red-600">{stopsError}</p>
                                        <p className="text-[11px] text-slate-500 mt-1">This is a load failure, not an empty day.</p>
                                    </div>
                                ) : stopsLoading ? (
                                    <div className="px-4 py-10 text-center text-slate-400">
                                        <div className="w-8 h-8 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin mx-auto mb-2" />
                                        <p className="text-[11px] font-bold uppercase tracking-widest">Grouping pings…</p>
                                    </div>
                                ) : stops.length === 0 ? (
                                    <div className="px-4 py-10 text-center text-slate-400 text-sm font-medium">
                                        No stop lasted 3 minutes on this day.
                                    </div>
                                ) : (
                                    <ol className="divide-y divide-slate-100">
                                        {stops.map(s => (
                                            <li key={s.seq} onClick={() => setFlyTarget([s.lat, s.lng])}
                                                className="px-3 py-2.5 flex gap-3 hover:bg-indigo-50/60 cursor-pointer">
                                                <span className="mt-0.5 w-6 h-6 rounded-full bg-indigo-600 text-white text-[11px] font-black flex items-center justify-center flex-shrink-0">
                                                    {s.seq}
                                                </span>
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-baseline justify-between gap-2">
                                                        <span className="text-sm font-black text-slate-900">{clock(s.arrived_at)}</span>
                                                        <span className="text-[11px] font-bold text-indigo-600 whitespace-nowrap">
                                                            {s.minutes >= 60 ? `${Math.floor(s.minutes / 60)}h ${s.minutes % 60}m` : `${s.minutes}m`} here
                                                        </span>
                                                    </div>
                                                    <p className="text-[11px] text-slate-500 leading-snug mt-0.5 break-words">
                                                        {s.address || `${s.lat.toFixed(5)}, ${s.lng.toFixed(5)}`}
                                                    </p>
                                                    <p className="text-[10px] text-slate-400 mt-0.5">
                                                        left {clock(s.left_at)}
                                                        {s.travel_minutes > 0 && ` · ${s.travel_minutes}m travel to get here`}
                                                    </p>
                                                </div>
                                            </li>
                                        ))}
                                    </ol>
                                )}
                            </div>
                        </div>
                    )}

                    {mode === 'tracking' && selected && !historyLoading && history.length === 0 && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <p className="bg-white/95 px-4 py-2 rounded-xl shadow text-sm font-medium text-slate-500">
                                No tracking recorded for {selected.name} on this day.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LiveLocation;
